import { Link, NavLink } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth";
import { visibleModules } from "@/lib/nav";
import { cn } from "@/lib/utils";

export const LogoMark = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" className="tl-mark shrink-0" aria-hidden="true" data-testid="logo-mark">
    <path d="M8.78 21.55A13 13 0 1 1 15.51 37.78" stroke="#1F4D3A" strokeWidth="4" strokeLinecap="round" />
    <path d="M10 27l6.5 6.5L30 19" stroke="#1F4D3A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 15h38M42 15l-3.5 9M42 15l3.5 9" stroke="#1F4D3A" strokeWidth="2" strokeLinecap="round" />
    <path d="M37.5 24h9a4.5 4.5 0 0 1-9 0z" fill="#1F4D3A" />
  </svg>
);

export const Logo = ({ compact }) => (
  <span className="flex items-center gap-2" data-testid="brand-logo">
    <LogoMark />
    {!compact && <span className="tl-wordmark font-display text-xl font-semibold tracking-tight text-ink">Onboard</span>}
  </span>
);

function NavItem({ m, collapsed }) {
  const link = (
    <NavLink
      to={m.path}
      end={m.path === "/app"}
      data-testid={`nav-${m.key}`}
      data-tour={m.key === "settings" ? "settings" : undefined}
      className={({ isActive }) =>
        cn(
          "relative flex h-9 items-center gap-3 rounded-[4px] px-3 text-sm transition-colors duration-150",
          isActive ? "font-medium text-brand before:absolute before:inset-y-1.5 before:left-0 before:w-[2px] before:rounded-full before:bg-brand" : "text-ink-secondary hover:bg-[#EFEBE3] hover:text-ink",
          collapsed && "justify-center px-0"
        )
      }
    >
      <m.icon size={18} strokeWidth={1.5} className="shrink-0" />
      {!collapsed && <span>{m.label}</span>}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="bg-ink text-white">{m.label}</TooltipContent>
    </Tooltip>
  );
}

function Group({ label, items, collapsed, tour }) {
  if (!items.length) return null;
  return (
    <div data-tour={tour} data-testid={`sidebar-group-${tour}`} className="space-y-1">
      {!collapsed && <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-muted">{label}</p>}
      {items.map((m) => <NavItem key={m.key} m={m} collapsed={collapsed} />)}
    </div>
  );
}

export function Sidebar({ collapsed, onToggle, className, drawer }) {
  const { user } = useAuth();
  const mods = visibleModules(user.role);
  const by = (g) => mods.filter((m) => m.group === g);
  return (
    <aside
      data-testid={drawer ? "mobile-sidebar" : "app-sidebar"}
      className={cn(
        "sticky top-0 flex h-screen shrink-0 flex-col border-r border-line bg-canvas px-3 py-4 transition-[width] duration-200",
        collapsed ? "w-[68px]" : "w-[240px]",
        className
      )}
    >
      <Link to="/" data-testid="sidebar-logo-link" className={cn("mb-8 flex px-2", collapsed && "justify-center px-0")}>
        <Logo compact={collapsed} />
      </Link>
      <nav className="flex flex-1 flex-col gap-8">
        <Group label="Daily work" items={by("daily")} collapsed={collapsed} tour="sidebar-daily" />
        <Group label="Pipeline tools" items={by("pipeline")} collapsed={collapsed} tour="sidebar-pipeline" />
        <div className="mt-auto space-y-1">
          {by("bottom").map((m) => <NavItem key={m.key} m={m} collapsed={collapsed} />)}
          <button
            onClick={onToggle}
            data-testid={drawer ? "mobile-drawer-close" : "sidebar-collapse-toggle"}
            className={cn(
              "flex h-9 w-full items-center gap-3 rounded-[4px] px-3 text-sm text-ink-muted hover:bg-[#EFEBE3] hover:text-ink transition-colors duration-150",
              collapsed && "justify-center px-0"
            )}
          >
            {drawer ? <X size={18} /> : collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            {!collapsed && <span>{drawer ? "Close menu" : "Collapse"}</span>}
          </button>
        </div>
      </nav>
    </aside>
  );
}
